// app/(auth)/onboarding.tsx
import AppScreen from "@/components/default/AppScreen";
import { Button, Input, Select, TextArea } from "@/components/default/forms";
import { useAppTheme } from "@/hooks/useAppTheme";
import { StatusBar } from "expo-status-bar";
import { useState } from "react";
import {
  Alert,
  Dimensions,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

const { width } = Dimensions.get("window");

// Mock API functions (replace with real backend calls)
const mockSendOtp = async (email: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      if (email && email.includes("@")) {
        Alert.alert("OTP Sent", `Demo OTP: 123456 to ${email}`);
        resolve();
      } else {
        reject(new Error("Invalid email"));
      }
    }, 1000);
  });
};

const mockVerifyOtp = async (otp: string): Promise<boolean> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(otp === "123456"); // Demo: accept "123456"
    }, 1000);
  });
};

const mockRegisterOrganization = async (data: any): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      console.log("Registration data:", data);
      resolve();
    }, 1500);
  });
};

const businessTypeOptions = [
  { label: "Retail", value: "retail" },
  { label: "Manufacturing", value: "manufacturing" },
  { label: "Services", value: "services" },
  { label: "Healthcare", value: "healthcare" },
  { label: "Education", value: "education" },
  { label: "Technology", value: "technology" },
  { label: "Other", value: "other" },
];

export default function OnboardingScreen() {
  const { colors, spacing, mode } = useAppTheme();

  // Step management: 'details' first, then 'emailVerification'
  const [step, setStep] = useState<"details" | "emailVerification">("details");

  // Organization & owner details state (all fields, including email)
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [orgName, setOrgName] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [gstin, setGstin] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [isSubmittingDetails, setIsSubmittingDetails] = useState(false);

  // Email verification state
  const [otp, setOtp] = useState("");
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpError, setOtpError] = useState("");

  // Validation errors for details form
  const [detailsErrors, setDetailsErrors] = useState({
    fullName: "",
    email: "",
    password: "",
    orgName: "",
    businessType: "",
    phone: "",
    address: "",
  });

  const handleBusinessTypeChange = (value: string | number) => {
    setBusinessType(String(value));
  };

  // Validate details form
  const validateDetails = () => {
    let valid = true;
    const errors = {
      fullName: "",
      email: "",
      password: "",
      orgName: "",
      businessType: "",
      phone: "",
      address: "",
    };

    if (!fullName.trim()) {
      errors.fullName = "Full name is required";
      valid = false;
    }
    if (!email.trim()) {
      errors.email = "Email is required";
      valid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = "Enter a valid email";
      valid = false;
    }
    if (!password) {
      errors.password = "Password is required";
      valid = false;
    } else if (password.length < 6) {
      errors.password = "Password must be at least 6 characters";
      valid = false;
    }
    if (!orgName.trim()) {
      errors.orgName = "Organization name is required";
      valid = false;
    }
    if (!businessType) {
      errors.businessType = "Please select a business type";
      valid = false;
    }
    if (!phone.trim()) {
      errors.phone = "Phone number is required";
      valid = false;
    } else if (!/^\d{10}$/.test(phone)) {
      errors.phone = "Enter a valid 10-digit phone number";
      valid = false;
    }
    if (!address.trim()) {
      errors.address = "Address is required";
      valid = false;
    }

    setDetailsErrors(errors);
    return valid;
  };

  // Handle submit details – move to email verification step
  const handleSubmitDetails = () => {
    if (validateDetails()) {
      setIsSubmittingDetails(true);
      // Simulate saving details locally or just move to next step
      setTimeout(() => {
        setIsSubmittingDetails(false);
        setStep("emailVerification");
      }, 500);
    }
  };

  // ----- Email verification (step 2) -----
  const handleSendOtp = async () => {
    if (!email.trim()) {
      Alert.alert("Error", "Email is missing. Please go back and fill email.");
      return;
    }
    setOtpError("");
    setIsSendingOtp(true);
    try {
      await mockSendOtp(email);
      Alert.alert("OTP Sent", `Please check your email (demo OTP: 123456).`);
    } catch (error) {
      Alert.alert("Error", "Failed to send OTP. Please try again.");
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp.trim()) {
      setOtpError("OTP is required");
      return;
    }
    setOtpError("");
    setIsVerifyingOtp(true);
    try {
      const isValid = await mockVerifyOtp(otp);
      if (isValid) {
        // OTP verified – register organization and go to subscription
        const payload = {
          fullName,
          email,
          password,
          orgName,
          businessType,
          gstin: gstin || null,
          phone,
          address,
        };
        await mockRegisterOrganization(payload);
        Alert.alert(
          "Success",
          "Organization registered successfully!",
          //   [
          //   {
          //     text: "Continue to Subscription",
          //     onPress: () => {
          //       // router.push("/(auth)/subscription");
          //       console.log("Navigate to subscription page");
          //     },
          //   },
          // ]
        );
      } else {
        setOtpError("Invalid OTP. Please try again.");
      }
    } catch (error) {
      Alert.alert("Error", "Registration failed. Please try again.");
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  // ----- Render step 1: Organization & Owner Details -----
  const renderDetailsStep = () => (
    <View style={styles.card}>
      <View style={styles.headerContainer}>
        <Text style={styles.title}>Organization Setup</Text>
        <Text style={styles.subtitle}>
          Tell us about your business and create an owner account
        </Text>
      </View>

      <Input
        label="Full Name (Owner)"
        value={fullName}
        onChangeText={setFullName}
        placeholder="John Doe"
        error={detailsErrors.fullName}
        containerStyle={styles.inputContainer}
      />

      <Input
        label="Email Address"
        value={email}
        onChangeText={setEmail}
        placeholder="you@company.com"
        keyboardType="email-address"
        autoCapitalize="none"
        error={detailsErrors.email}
        containerStyle={styles.inputContainer}
      />

      <Input
        label="Password"
        value={password}
        onChangeText={setPassword}
        placeholder="Create a strong password"
        secureTextEntry
        error={detailsErrors.password}
        containerStyle={styles.inputContainer}
      />

      <Input
        label="Organization Name"
        value={orgName}
        onChangeText={setOrgName}
        placeholder="Acme Inc."
        error={detailsErrors.orgName}
        containerStyle={styles.inputContainer}
      />

      <Select
        label="Business Type"
        options={businessTypeOptions}
        value={businessType}
        onValueChange={handleBusinessTypeChange}
        placeholder="Select a business type"
        error={detailsErrors.businessType}
        containerStyle={styles.inputContainer}
      />

      <Input
        label="GSTIN (Optional)"
        value={gstin}
        onChangeText={setGstin}
        placeholder="22AAAAA0000A1Z"
        containerStyle={styles.inputContainer}
      />

      <Input
        label="Phone Number"
        value={phone}
        onChangeText={setPhone}
        placeholder="9876543210"
        keyboardType="phone-pad"
        error={detailsErrors.phone}
        containerStyle={styles.inputContainer}
      />

      <TextArea
        label="Address"
        value={address}
        onChangeText={setAddress}
        placeholder="Street, city, pincode"
        numberOfLines={3}
        error={detailsErrors.address}
        containerStyle={styles.inputContainer}
      />

      <Button
        title="Continue to Email Verification"
        onPress={handleSubmitDetails}
        loading={isSubmittingDetails}
        variant="primary"
        containerStyle={styles.button}
      />
    </View>
  );

  // ----- Render step 2: Email Verification (OTP) -----
  const renderEmailVerificationStep = () => (
    <View style={styles.card}>
      <View style={styles.headerContainer}>
        <Text style={styles.title}>Verify Your Email</Text>
        <Text style={styles.subtitle}>
          We've sent a one‑time password to {email}
        </Text>
      </View>

      <Input
        label="One-Time Password"
        value={otp}
        onChangeText={setOtp}
        placeholder="Enter 6-digit OTP"
        keyboardType="number-pad"
        error={otpError}
        containerStyle={styles.inputContainer}
      />

      <Button
        title="Send OTP"
        onPress={handleSendOtp}
        loading={isSendingOtp}
        variant="secondary"
        containerStyle={styles.buttonOutline}
      />

      <Button
        title="Verify & Complete Registration"
        onPress={handleVerifyOtp}
        loading={isVerifyingOtp}
        variant="primary"
        containerStyle={styles.button}
      />
    </View>
  );

  // Dynamic styles based on theme
  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    keyboardView: {
      flex: 1,
    },
    scrollContent: {
      flexGrow: 1,
      justifyContent: "center",
      alignItems: "center",
      paddingVertical: spacing.xl,
    },
    card: {
      width: width * 0.9,
      maxWidth: 500,
      backgroundColor: colors.background,
      borderRadius: 16,
      paddingVertical: spacing.xxl,
      paddingHorizontal: spacing.xl,
      shadowColor: mode === "light" ? "#000" : "#fff",
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.05,
      shadowRadius: 8,
      elevation: 3,
      borderWidth: 1,
      borderColor: colors.border,
    },
    headerContainer: {
      marginBottom: spacing.xxl,
      alignItems: "center",
    },
    title: {
      fontSize: 26,
      fontWeight: "600",
      color: colors.text,
      marginBottom: spacing.sm,
    },
    subtitle: {
      fontSize: 14,
      color: colors.textLight,
      textAlign: "center",
    },
    inputContainer: {
      marginBottom: spacing.lg,
    },
    button: {
      width: "100%",
      marginTop: spacing.sm,
    },
    buttonOutline: {
      width: "100%",
      marginTop: spacing.sm,
      marginBottom: spacing.md,
    },
  });

  return (
    <AppScreen>
      <View style={styles.container}>
        <StatusBar style={mode === "light" ? "dark" : "light"} />
        <KeyboardAvoidingView
          behavior={Platform.OS === "ios" ? "padding" : "height"}
          style={styles.keyboardView}
        >
          <ScrollView
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
          >
            {step === "details"
              ? renderDetailsStep()
              : renderEmailVerificationStep()}
          </ScrollView>
        </KeyboardAvoidingView>
      </View>
    </AppScreen>
  );
}
